<template>
  <v-dialog
    :value="value != null"
    persistent
    max-width="1000px"
    content-class="quote-image-dialog"
  >
    <v-card class="quote-image-dialog__card">
      <v-card-title class="quote-image-dialog__header">
        Select Image
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="quote-image-dialog__search pa-4 pb-2">
        <div class="d-flex align-center">
          <a-text-field v-model="searchQuery" label="Search" smlabel />
          <v-btn v-if="searchQuery" icon small @click="searchQuery = ''"><v-icon>close</v-icon></v-btn>
        </div>
      </v-card-text>

      <v-card-text class="quote-image-dialog__grid pa-4 pt-2">
        <v-row>
          <v-col v-for="(image, index) in filteredImages" :key="index" cols="12" sm="6" md="4" lg="3">
            <v-card
              flat
              outlined
              class="image-card"
              :class="{ 'selected-image': selectedImageId === image.id }"
              @click="selectedImageId = image.id"
              @dblclick="selectImage(image.id)"
            >
              <v-img :src="getImagePath(image)" height="150px" contain class="grey lighten-4" />
              <v-card-text class="text-center py-2 text-truncate">
                {{ image.name }}
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <div v-if="filteredImages.length === 0" class="text-center py-8 grey--text">No images available.</div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-text class="quote-image-dialog__upload pa-4">
        <div class="subtitle-2 mb-2">Upload New Image</div>
        <v-row align="center" dense>
          <v-col cols="12" md="6">
            <v-file-input
              v-model="uploadFile"
              label="Choose Image"
              prepend-icon="mdi-camera"
              accept="image/*"
              outlined
              dense
              hide-details
              :loading="uploadInProgress"
              :disabled="uploadInProgress"
              @change="onFileSelected"
            ></v-file-input>
          </v-col>
          <v-col cols="12" md="4">
            <a-text-field
              v-model="uploadFileName"
              label="Name"
              :filenameSafe="true"
              :disabled="!uploadFile || uploadInProgress"
            />
          </v-col>
          <v-col cols="12" md="2" class="d-flex align-center">
            <g-button
              label="Upload"
              type="primary"
              :disabled="!canUpload"
              @onclick="onUpload"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="quote-image-dialog__actions">
        <g-button label="Cancel" @onclick="close" />
        <v-spacer></v-spacer>
        <g-button label="Select" type="primary" :disabled="selectedImageId === null" @onclick="select" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  props: {
    value: Object,
  },
  data() {
    return {
      images: [],
      searchQuery: "",
      selectedImageId: null,
      uploadFile: null,
      uploadFileName: "",
      uploadInProgress: false,
    };
  },
  computed: {
    filteredImages() {
      if (!this.searchQuery) return this.images;
      const query = this.searchQuery.toLowerCase();
      return this.images.filter((image) => {
        const nameMatch = image.name && image.name.toLowerCase().includes(query);
        const descMatch = image.description && image.description.toLowerCase().includes(query);
        return nameMatch || descMatch;
      });
    },
    canUpload() {
      return !!this.uploadFile && !!this.uploadFileName && !this.uploadInProgress;
    },
  },
  methods: {
    close() {
      this.$emit("input", null);
    },
    select() {
      this.selectImage(this.selectedImageId);
    },
    selectImage(imageId) {
      if (imageId == null) return;
      const selectedImage = this.images.find((img) => img.id === imageId);
      if (!selectedImage) return;
      this.selectedImageId = imageId;
      this.$emit("select", selectedImage);
      this.close();
    },
    sanitizeName(name) {
      return String(name || "")
        .split(".")[0]
        .replace(/ /g, "-")
        .replace(/[^a-zA-Z0-9_-]/g, "");
    },
    onFileSelected(fileOrFiles) {
      const file = Array.isArray(fileOrFiles) ? fileOrFiles[0] : fileOrFiles;
      if (!file || !file.name) {
        this.uploadFileName = "";
        return;
      }
      this.uploadFileName = this.sanitizeName(file.name);
    },
    onUpload() {
      const file = Array.isArray(this.uploadFile) ? this.uploadFile[0] : this.uploadFile;
      if (!file || !file.name || !this.uploadFileName) return;

      const imageName = this.sanitizeName(this.uploadFileName) || this.sanitizeName(file.name);
      this.uploadInProgress = true;
      this.$store
        .dispatch("StorageStore/uploadFile", {
          name: imageName,
          file,
        })
        .then((data) => {
          if (!data?.key) {
            throw "Upload failed: missing storage key";
          }
          return this.$store.dispatch("ImageStore/addImage", {
            description: imageName,
            key: data.key,
            filename: data.filename,
            mimetype: data.mimetype,
            size: data.size,
          });
        })
        .then(() => {
          this.uploadFile = null;
          this.uploadFileName = "";
          this.fetchImages();
          this.$root.$success("Image uploaded successfully");
        })
        .catch((err) => {
          const message =
            typeof err === "string"
              ? err
              : err?.message || err?.data || (err && JSON.stringify(err) !== "{}" ? err : null) || "Image upload failed";
          this.$root.$error(message);
        })
        .finally(() => {
          this.uploadInProgress = false;
        });
    },
    fetchImages() {
      const query = { multiple: true, list: [] };
      this.$store.dispatch("ImageStore/getImageList", { query }).then((images) => {
        this.images = images || [];
        this.selectedImageId = this.value.imageId;
      });
    },
    getImagePath(image) {
      if (!image?.name) return "";
      return `${this.$store.getters["host"]}storage/images/${image.name}`;
    },
  },
  watch: {
    value: {
      handler(val) {
        if (val) {
          this.fetchImages();
        }
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
.quote-image-dialog__card {
  display: flex;
  flex-direction: column;
  max-height: min(90vh, 800px);
  padding: 0;
  overflow: hidden;
}

.quote-image-dialog__header,
.quote-image-dialog__search,
.quote-image-dialog__upload,
.quote-image-dialog__actions {
  flex: 0 0 auto;
}

.quote-image-dialog__grid {
  flex: 1 1 auto;
  min-height: 120px;
  overflow-y: auto;
}

.image-card {
  cursor: pointer;
  transition: all 0.2s;
}
.image-card:hover {
  border-color: black;
  background-color: rgba(0, 0, 0, 0.02);
}
.selected-image {
  border: 1px solid black !important;
  background-color: rgba(0, 0, 0, 0.05);
}
</style>

<style>
/* content-class is applied outside the scoped root */
.quote-image-dialog {
  margin: 12px !important;
  max-height: 90vh;
  overflow: hidden;
}

.quote-image-dialog.v-dialog {
  display: flex;
  flex-direction: column;
}

.quote-image-dialog > .v-card {
  max-height: 90vh;
}
</style>
